import { useAuth } from "@elit-hair/elit-tools";
import { Icon, Label, Switch } from "@elit-hair/elit-ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCrown, TbUser, TbUserOff } from "react-icons/tb";

import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { useInfiniteUsers } from "@/hooks/queries/user.queries";
import { cn } from "@/lib/utils";
import { useAccessibleTeams } from "@/pages/home/components/role-dashboard/hooks/use-accessible-teams";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/use-dashboard-role";
import { TeamRole } from "@/types/team.types";
import { canAccessTeamMemberSelection } from "@/utils/team.utils";

import { DoubleSelect, type DoubleSelectGroups, type Group, type Item } from "./double-select";

const USERS_PAGE_SIZE = 100;

type TeamMemberSelectorLabels = {
    selectGroupAndItem?: string;
    selectGroup?: string;
    selectItem?: string;
    selectItemFromGroup?: string;
    loading?: string;
    items?: string;
    noItemsInGroup?: string;
    page?: string;
    of?: string;
    confirm?: string;
    resetSelection?: string;
};

export type TeamMemberSelectorProps = {
    selectedMemberId?: string | null;
    onMemberChange?: (memberId: string | null) => void;
    currentUserId?: string | null;
    disabled?: boolean;
    /** Users who belong to no team. Hidden when the caller may only read their own teams. */
    includeUsersWithNoTeam?: boolean;
    labels?: TeamMemberSelectorLabels;
    // Open the member dialog from outside and optionally hide the built-in trigger
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    hideTrigger?: boolean;
};

/**
 * Team Member Selector component that handles team filtering based on user permissions
 * and provides a double-select interface for choosing team members
 */
const defaultLabels: TeamMemberSelectorLabels = {
    selectGroupAndItem: "select_team_and_member",
    selectGroup: "select_team",
    selectItem: "select_member",
    selectItemFromGroup: "select_member_from_team",
    loading: "loading",
    items: "members",
    noItemsInGroup: "no_members_in_team",
    page: "page",
    of: "of",
    confirm: "confirm",
    resetSelection: "my_calls",
};

export const TeamMemberSelector = ({
    selectedMemberId,
    onMemberChange,
    currentUserId,
    disabled = false,
    includeUsersWithNoTeam = true,
    labels: labelsOverride,
    open,
    onOpenChange,
    hideTrigger = false,
}: TeamMemberSelectorProps) => {
    const { userData } = useAuth();
    const { permissionCodes, isPreview } = useDashboardRole();
    const { t } = useTranslation();
    const [showDisabledUsers, setShowDisabledUsers] = useState(false);
    const { data: allTeams, isLoading: isLoadingTeams } = useTeams();
    const {
        data: usersPages,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading: isLoadingUsers,
        isError: isUsersError,
    } = useInfiniteUsers({ limit: USERS_PAGE_SIZE }, { enabled: includeUsersWithNoTeam });
    const effectivePermissionCodes = isPreview ? permissionCodes : (userData?.permissionCodes ?? []);
    const accessibleTeams = useAccessibleTeams(allTeams?.data || []);

    useEffect(() => {
        if (hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    const filteredTeams = useMemo(() => {
        if (!allTeams?.data) return allTeams;

        if (isPreview) {
            return { ...allTeams, data: accessibleTeams };
        }

        if (!canAccessTeamMemberSelection(effectivePermissionCodes)) {
            return { ...allTeams, data: [] };
        }

        const allowedIds = new Set(accessibleTeams.map((t) => t.id));
        const filteredTeamsData = allTeams.data.filter((team) => allowedIds.has(team.id));

        return { ...allTeams, data: filteredTeamsData };
    }, [allTeams, effectivePermissionCodes, accessibleTeams, isPreview]);

    // Disabled users are hidden unless toggled on; the selected member stays visible so the selection is kept
    const isItemVisible = useCallback(
        (item: Item) => showDisabledUsers || !item.disabled || item.id === selectedMemberId,
        [showDisabledUsers, selectedMemberId]
    );

    // Transform teams to groups
    const teams: DoubleSelectGroups | undefined = useMemo(() => {
        if (!filteredTeams) return undefined;

        return {
            data: filteredTeams.data.map(
                (team): Group => ({
                    id: team.id,
                    name: team.name,
                    color: team.color,
                    items: team.members
                        .map(
                            (member): Item => ({
                                id: member.id,
                                name: member.user?.name,
                                email: member.user?.email,
                                role: member.role,
                                isLeader: member.role === TeamRole.TEAM_LEAD,
                                disabled: member.user?.disabled ?? false,
                            })
                        )
                        .filter(isItemVisible),
                })
            ),
        };
    }, [filteredTeams, isItemVisible]);

    const usersWithNoTeam = useMemo(() => {
        if (!usersPages || hasNextPage || isUsersError) {
            return undefined;
        }

        const assignedUserIds = new Set<string>();
        for (const team of allTeams?.data ?? []) {
            for (const member of team.members) {
                assignedUserIds.add(member.id);
                if (member.user?.id) {
                    assignedUserIds.add(member.user.id);
                }
            }
        }

        return usersPages.pages.flatMap((page) => page.users).filter((user) => !assignedUserIds.has(user.id));
    }, [allTeams?.data, hasNextPage, isUsersError, usersPages]);

    const usersWithNoTeamGroup = useMemo((): Group | undefined => {
        if (!usersWithNoTeam) {
            return undefined;
        }

        return {
            id: "users-with-no-team",
            name: t("users_with_no_team"),
            items: usersWithNoTeam
                .map(
                    (user): Item => ({
                        id: user.id,
                        name: user.name,
                        email: user.email,
                        disabled: user.disabled,
                    })
                )
                .filter(isItemVisible),
        };
    }, [isItemVisible, t, usersWithNoTeam]);

    const labels = { ...defaultLabels, ...labelsOverride };
    const disabledClassName = cn(disabled && "pointer-events-none cursor-not-allowed opacity-50");

    // Mount DoubleSelect only after teams are loaded. While groups are empty it clears "my calls" and never restores it.
    if (isLoadingTeams) {
        if (hideTrigger) return null;
        return (
            <div
                className={cn(
                    "border border-border p-2 sm:p-2.5 rounded-md w-full flex items-center min-h-10",
                    disabledClassName
                )}
                aria-disabled={disabled}
            >
                <div className="text-muted-foreground text-xs">{t(labels.loading || "loading")}</div>
            </div>
        );
    }

    return (
        <div className={disabledClassName} aria-disabled={disabled}>
            <DoubleSelect
                selectedItemId={selectedMemberId}
                onItemChange={disabled ? undefined : onMemberChange}
                currentUserId={currentUserId}
                groups={teams}
                bottomGroup={includeUsersWithNoTeam ? usersWithNoTeamGroup : undefined}
                isLoadingGroups={false}
                isLoadingBottomGroup={isLoadingUsers || isFetchingNextPage || Boolean(hasNextPage)}
                getItemIcon={(item) => {
                    const isDisabled = item.disabled === true;
                    if (isDisabled) {
                        return (
                            <Icon icon={TbUserOff} size={16} className="shrink-0 text-muted-foreground opacity-70" />
                        );
                    }
                    const isLeader = item.isLeader || item.role === TeamRole.TEAM_LEAD;
                    return isLeader ? (
                        <Icon icon={TbCrown} size={16} className="shrink-0 text-yellow-500" />
                    ) : (
                        <Icon icon={TbUser} size={16} className="shrink-0 text-muted-foreground" />
                    );
                }}
                getItemDisplayText={(item) => {
                    const isDisabled = item.disabled === true;
                    const isLeader = item.isLeader || item.role === TeamRole.TEAM_LEAD;
                    const hasRole = isLeader || item.role === TeamRole.AGENT;
                    if (!hasRole) {
                        return isDisabled ? t("user_disabled") : "";
                    }
                    const roleLabel = isLeader ? t("team_lead") : t("agent");
                    return isDisabled ? `${roleLabel} • ${t("user_disabled")}` : roleLabel;
                }}
                labels={labels}
                open={open}
                onOpenChange={onOpenChange}
                hideTrigger={hideTrigger}
                headerActions={
                    <>
                        <Switch
                            id="team-member-selector-show-disabled"
                            checked={showDisabledUsers}
                            onCheckedChange={setShowDisabledUsers}
                        />
                        <Label
                            htmlFor="team-member-selector-show-disabled"
                            className="cursor-pointer text-xs font-normal"
                        >
                            {t("show_disabled_users")}
                        </Label>
                    </>
                }
            />
        </div>
    );
};
